import { useMemo, useState } from 'react';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import produce from 'immer';
import { faTrash, faUserSlash } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import type { Maybe } from '@soundxyz/utils';
import { ERROR_TYPE, PILLARS, POSTING_ERROR_ACTIONS } from '@soundxyz/utils/constants/logging';
import type { ContentLexicalStateV1 } from '@soundxyz/utils/lexical/ContentLexicalStateV1';
import { useIsChannelCurator } from '@components/Channels/useIsChannelCurator';
import { CommentButton } from '@components/Feed/Post/CommentButton';
import { AdminDeletePostCommentReplyModal } from '@components/Feed/Post/DeletePostModal';
import { UpvoteButton } from '@components/Feed/Post/UpvoteButton';
import { LexicalDisplay } from '@components/Lexical/Display';
import { DropdownMenuItem } from '@components/basics/Dropdowns/GlobalDropdown';
import { ThreeDotsDropdown } from '@components/basics/Dropdowns/ThreeDotsDropdown';
import { Avatar } from '@components/basics/Images/Avatar';
import { Timestamp } from '@components/basics/Timestamp';
import { ReplyCommentWithParentCommentModal } from '@components/modals/ReplyModal/ReplyCommentWithParentCommentModal';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import { ModalType, useModal } from '@context/modal';
import { useToast } from '@context/toast';
import {
  CommentAuthorFragmentDoc,
  CommentRepliesDocument,
  CurrentUserPostVoteStatus,
  DeletePostCommentReplyDocument,
  type FragmentType,
  getFragment,
  gql,
  PostVoteChange,
  setInfiniteQueryData,
  useMutation,
  useQuery,
  UserVoteOnPostCommentDocument,
  VoteOnPostCommentDocument,
} from '@graphql/reactQuery';
import { useIsHydrated } from '@hooks/useIsHydrated';
import { useLogError } from '@hooks/useLogErrorNew';
import { useStableCallback } from '@hooks/useStableCallback';
import { webappUriPath } from '@utils/url';
import { ConfirmModal } from '../confirm-modal';
import { AccessibleLink } from '../link/AccessibleLink';
import { FeedCuratorTooltip } from '../tooltip/FeedCuratorTooltip';
import { VerifiedTooltip } from '../tooltip/VerifiedTooltip';
import { commentReplyFilterQueryKey, setVoteStatus, useBatchedKey } from './shared';

/*#__PURE__*/ gql(/* GraphQL */ `
  mutation DeletePostCommentReply($input: MutationDeletePostCommentReplyInput!) {
    deletePostCommentReply(input: $input) {
      __typename
      ... on MutationDeletePostCommentReplySuccess {
        data {
          id
        }
      }
      ... on Error {
        message
      }
    }
  }
`);

type UserReplyItemProps = {
  createdAt: string;
  message: string | ContentLexicalStateV1;
  parentCommentId: string;
  replyId: string;
  showArtistTag: boolean;
  showCollectorTag: boolean;
  showFeedCuratorBadge: boolean;
  showVerifiedBadge: boolean;
  upvoteCount: number;
  userFragment: FragmentType<CommentAuthorFragmentDoc>;
  channelId: Maybe<string>;
};

export const UserReplyItem = ({
  createdAt,
  message,
  parentCommentId,
  replyId,
  showArtistTag,
  showCollectorTag,
  showFeedCuratorBadge,
  showVerifiedBadge,
  upvoteCount,
  userFragment,
  channelId,
}: UserReplyItemProps) => {
  const { authUser, isAuthenticatedWithAPI } = useAuth();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { openModal, closeModal } = useModal();
  const { openToast } = useToast();

  const user = getFragment(CommentAuthorFragmentDoc, userFragment);
  const { username, webappUri, publicAddress, avatar } = user;
  const userWebappUri = webappUriPath(webappUri);

  const { isChannelCurator, isChannelOwner } = useIsChannelCurator({ channelId });

  const postCommentIds = useBatchedKey({ key: replyId });

  const hydrated = useIsHydrated();

  const logError = useLogError();

  const showTag = showArtistTag || showCollectorTag;

  const { data: voteData } = useQuery(UserVoteOnPostCommentDocument, {
    variables: !!postCommentIds && {
      input: { postCommentIds },
    },
    cacheTime: Infinity,
    select({ data }) {
      for (const statusData of data.userVoteOnPostComment) {
        if (statusData.postCommentId === replyId)
          return { voteStatus: statusData.voteStatus, upvoteCount: statusData.upvoteCount };
      }
      return;
    },
    enabled: isAuthenticatedWithAPI && !!postCommentIds?.length,
    keepPreviousData: true,
    staleTime: 0,
  });

  const { mutateAsync: voteOnPost, isLoading: isVoting } = useMutation(VoteOnPostCommentDocument, {
    retry: 3,
  });

  const { mutateAsync: deleteComment, isLoading: isDeleting } = useMutation(
    DeletePostCommentReplyDocument,
    {
      retry: 3,
    },
  );

  const handleReplyClicked = useStableCallback(() => {
    if (!isAuthenticatedWithAPI) {
      triggerLogin();
      return;
    }

    openModal(ModalType.REPLY_POST, {
      body: (
        <ReplyCommentWithParentCommentModal
          postCommentId={replyId}
          parentPostCommentId={parentCommentId}
        />
      ),
    });
  });

  const handleUpvoteClicked = useStableCallback(async () => {
    if (!isAuthenticatedWithAPI) {
      triggerLogin();
      return;
    }

    if (isVoting || !voteData) return;

    const { voteStatus, upvoteCount } = voteData;

    const isUpvoting = voteStatus === CurrentUserPostVoteStatus.NoVote;

    setVoteStatus({
      postCommentId: replyId,
      voteStatus: isUpvoting ? CurrentUserPostVoteStatus.Upvoted : CurrentUserPostVoteStatus.NoVote,
      upvoteCount: isUpvoting ? upvoteCount + 1 : upvoteCount - 1,
    });

    try {
      const result = await voteOnPost({
        input: {
          postCommentId: replyId,
          voteStatus: isUpvoting ? PostVoteChange.Upvote : PostVoteChange.RemoveVote,
        },
      });

      if (result.data.voteOnPostComment?.__typename === 'NotFoundError') {
        logError({
          action: POSTING_ERROR_ACTIONS.UPVOTING_POST_COMMENT_FAILED,
          error: new Error(result.data.voteOnPostComment.message),
          errorType: ERROR_TYPE.UPVOTING_ERROR,
          isArtistOnlyPage: false,
          level: 'error',
          message: result.data.voteOnPostComment.message,
          toast: 'The comment you are trying to upvote does not exist.',
          indexedTags: {
            postCommentId: replyId,
            upvoteCount,
            voteStatus,
          },
          pillar: PILLARS.POSTING,
        });
      }

      if (result.data.voteOnPostComment?.__typename === 'RateLimitError') {
        logError({
          action: POSTING_ERROR_ACTIONS.UPVOTING_POST_COMMENT_FAILED,
          error: new Error(result.data.voteOnPostComment.message),
          errorType: ERROR_TYPE.UPVOTING_ERROR,
          isArtistOnlyPage: false,
          level: 'error',
          message: result.data.voteOnPostComment.message,
          toast: 'You are upvoting too fast, try again later please.',
          indexedTags: {
            postCommentId: replyId,
            upvoteCount,
            voteStatus,
          },
          pillar: PILLARS.POSTING,
        });
      }
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.UPVOTING_POST_COMMENT_FAILED,
        error,
        errorType: ERROR_TYPE.UPVOTING_ERROR,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error upvoting a post comment',
        indexedTags: {
          postCommentId: replyId,
          upvoteCount,
          voteStatus,
        },
        pillar: PILLARS.POSTING,
      });
    }
  });

  const upvoteCountMemoized = useMemo(() => {
    if (isAuthenticatedWithAPI && voteData) {
      return voteData.upvoteCount;
    } else {
      return upvoteCount;
    }
  }, [isAuthenticatedWithAPI, voteData, upvoteCount]);

  const isLoading = useMemo(() => {
    if (isAuthenticatedWithAPI) {
      return !Boolean(voteData);
    } else {
      return !hydrated;
    }
  }, [isAuthenticatedWithAPI, hydrated, voteData]);

  const deleteReply = useStableCallback(async () => {
    try {
      const response = await deleteComment({
        input: {
          replyPostCommentId: replyId,
        },
      });

      if (
        response.data.deletePostCommentReply.__typename === 'MutationDeletePostCommentReplySuccess'
      ) {
        setInfiniteQueryData(
          {
            query: CommentRepliesDocument,
            filterQueryKey: commentReplyFilterQueryKey(parentCommentId),
            options: {},
          },
          previousData => {
            if (!previousData) return previousData;

            return produce(previousData, draft => {
              for (const page of draft.pages) {
                let i = 0;
                for (const edge of page.data.commentReplies.edges) {
                  if (edge.node.id === replyId) {
                    page.data.commentReplies.edges.splice(i, 1);
                    return;
                  }
                  ++i;
                }
              }
            });
          },
        );

        openToast({
          text: 'The reply has been deleted',
          variant: 'success',
        });
      } else {
        logError({
          action: POSTING_ERROR_ACTIONS.DELETE_POST_COMMENT_FAILED,
          error: new Error(response.data.deletePostCommentReply.message),
          errorType: ERROR_TYPE.UNKNOWN,
          isArtistOnlyPage: false,
          level: 'error',
          message: response.data.deletePostCommentReply.message,
          toast: response.data.deletePostCommentReply.message,
          indexedTags: {
            postCommentId: replyId,
          },
          pillar: PILLARS.POSTING,
        });
      }
    } catch (error) {
      logError({
        action: POSTING_ERROR_ACTIONS.DELETE_POST_COMMENT_FAILED,
        error,
        errorType: ERROR_TYPE.UNKNOWN,
        isArtistOnlyPage: false,
        level: 'error',
        message: 'Error deleting a post comment reply',
        toast: 'There was an error deleting the reply. Please try again.',
        indexedTags: {
          postCommentId: replyId,
        },
        pillar: PILLARS.POSTING,
      });
    }

    closeModal();
  });

  return (
    <div className="min-h-10 flex w-full min-w-0 flex-col gap-1 rounded-md py-1.5">
      <div className="flex w-full min-w-0 justify-between">
        <div className="flex w-full min-w-0 max-w-[70%] items-center gap-2">
          <div className="shrink-0">
            <Avatar
              size={16}
              src={avatar?.url}
              walletAddress={publicAddress}
              username={username}
              borderRadius={1000}
              webappUri={userWebappUri}
            />
          </div>

          <div className="flex items-center gap-2 truncate text-base-s font-semibold text-base900">
            <div className="flex min-w-0 items-center gap-0.5">
              <AccessibleLink
                pathnameUri={userWebappUri}
                className="truncate font-base text-base-m font-semibold text-base900"
              >
                {username}
              </AccessibleLink>
              {showVerifiedBadge && <VerifiedTooltip size={14} />}
              {showFeedCuratorBadge && <FeedCuratorTooltip portal size={14} />}
            </div>

            <div className="flex items-center gap-2 font-base text-base-xs font-normal">
              {showTag && (
                <div className="flex-shrink-0 rounded-sm bg-base300 px-1 py-[2px] font-base text-base-xs font-normal text-base500">
                  {showArtistTag ? 'Artist' : 'Collector'}
                </div>
              )}
              <Timestamp
                timeStamp={createdAt}
                className="font-base !text-base-xs font-normal text-base500 duration-300 hover:text-base600"
              />
            </div>
          </div>
        </div>

        <div className="relative flex items-center gap-1">
          <div className="flex h-4 w-8">
            {(authUser?.id === user.id || authUser?.roles?.isAdmin || isChannelCurator) && (
              <div className="flex items-center">
                <ThreeDotsDropdown
                  setIsDropdownOpen={setIsDropdownOpen}
                  isDropdownOpen={isDropdownOpen}
                  dropdownProps={{
                    align: 'end',
                    queue: true,
                  }}
                >
                  {authUser?.id === user.id && (
                    <DropdownMenuItem
                      onClick={() => {
                        openModal(ModalType.DELETE_POST_COMMENT_CONFIRMATION, {
                          body: (
                            <ConfirmModal
                              onConfirm={deleteReply}
                              onCancel={closeModal}
                              title="Delete Comment?"
                              message="Are you sure you want to delete your comment? This action cannot be undone."
                              confirmText="Delete"
                              cancelText="Cancel"
                              loading={isDeleting}
                              destructive
                            />
                          ),
                        });
                      }}
                    >
                      <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                        <FontAwesomeIcon icon={faTrash} size="lg" />
                        Delete
                      </div>
                    </DropdownMenuItem>
                  )}
                  {authUser?.roles?.isAdmin && (
                    <>
                      {authUser?.id !== user.id && (
                        <DropdownMenuItem
                          onClick={() => {
                            openModal(ModalType.DELETE_POST_COMMENT_CONFIRMATION, {
                              body: <AdminDeletePostCommentReplyModal postCommentId={replyId} />,
                            });
                          }}
                        >
                          <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                            <FontAwesomeIcon icon={faTrash} size="lg" />
                            Delete (Admin)
                          </div>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => {
                          window.open(
                            `${window.location.origin}/admin/moderate/${user.id}`,
                            '_blank',
                          );
                        }}
                      >
                        <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                          <FontAwesomeIcon icon={faUserSlash} size="lg" />
                          Block User (Admin)
                        </div>
                      </DropdownMenuItem>
                    </>
                  )}
                  {isChannelCurator && (
                    <DropdownMenuItem
                      onClick={() => {
                        openModal(ModalType.DELETE_POST_COMMENT_CONFIRMATION, {
                          body: (
                            <ConfirmModal
                              onConfirm={deleteReply}
                              onCancel={closeModal}
                              title="Delete Comment?"
                              message="Are you sure you want to delete the comment? This action cannot be undone."
                              confirmText="Delete"
                              cancelText="Cancel"
                              loading={isDeleting}
                              destructive
                            />
                          ),
                        });
                      }}
                    >
                      <div className="flex items-center gap-3 font-base text-base-m font-medium text-destructive700">
                        <FontAwesomeIcon icon={faTrash} size="lg" />
                        Delete ({isChannelOwner ? 'Owner' : 'Moderator'})
                      </div>
                    </DropdownMenuItem>
                  )}
                </ThreeDotsDropdown>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="break-words font-base text-base-m text-base900">
        {typeof message === 'string' ? message : <LexicalDisplay state={message} />}
      </div>

      {isLoading ? (
        <div className="mt-1 w-10 animate-pulse rounded-md bg-neutral200" />
      ) : (
        <div className="mt-1 flex h-7 items-center gap-7">
          <div className="-mx-2 -my-1">
            <UpvoteButton
              disabled={false}
              upvoteCount={upvoteCountMemoized}
              voteStatus={voteData?.voteStatus}
              onClick={handleUpvoteClicked}
              iconSize={14}
            />
          </div>
          <div className="-mx-2 -my-1">
            <CommentButton variant="comment" onClick={handleReplyClicked} iconSize={14} />
          </div>
        </div>
      )}
    </div>
  );
};
